import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Trash2, Building2, Plus, X } from 'lucide-react'
import { supabase, isDemoMode } from '../../lib/supabase'
import { DemoNotice } from '../../lib/demo'
import SavedIndicator from '../../components/SavedIndicator'

const FIELDS = [
  ['name',            'Group name',         'text'],
  ['contact_name',    'Group contact name', 'text'],
  ['contact_email',   'Group contact email','email'],
  ['contact_phone',   'Group contact phone','tel'],
  ['billing_address', 'Billing address',    'textarea'],
  ['notes',           'Internal notes',     'textarea'],
]

export default function ClientGroupDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [form, setForm] = useState(null)
  const [brandProfiles, setBrandProfiles] = useState([])
  const [sites, setSites] = useState([])
  const [orphanSites, setOrphanSites] = useState([])
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(0)
  const [adding, setAdding] = useState(false)

  const load = async () => {
    if (isDemoMode) { setForm({}); return }
    const [gRes, brandRes, cRes] = await Promise.all([
      supabase.from('client_groups').select('*').eq('id', id).maybeSingle(),
      supabase.from('brand_settings').select('id, name, scope').order('scope').order('name'),
      supabase.from('companies').select('id, name, client_group_id, contact_name, contact_email').is('archived_at', null).order('name'),
    ])
    setForm(gRes.data)
    setBrandProfiles(brandRes.data || [])
    const all = cRes.data || []
    setSites(all.filter(c => c.client_group_id === id))
    setOrphanSites(all.filter(c => !c.client_group_id))
  }
  useEffect(() => { load() }, [id])

  const save = async () => {
    if (isDemoMode) return
    setSaving(true)
    const { id: _, created_at, updated_at, ...payload } = form
    const { error } = await supabase.from('client_groups').update(payload).eq('id', id)
    setSaving(false)
    if (error) return alert(error.message)
    setSavedAt(Date.now())
  }

  const archive = async () => {
    if (isDemoMode) return
    if (!confirm('Archive this group? Member sites remain but lose their group link.')) return
    await supabase.from('companies').update({ client_group_id: null }).eq('client_group_id', id)
    await supabase.from('client_groups').update({ archived_at: new Date().toISOString() }).eq('id', id)
    navigate('/admin/client-groups')
  }

  const addSite = async (companyId) => {
    if (isDemoMode || !companyId) return
    await supabase.from('companies').update({ client_group_id: id }).eq('id', companyId)
    setAdding(false)
    load()
  }

  const removeSite = async (companyId) => {
    if (isDemoMode) return
    if (!confirm('Remove this site from the group?')) return
    await supabase.from('companies').update({ client_group_id: null }).eq('id', companyId)
    load()
  }

  if (!form) return <div className="card"><p className="text-sm text-gray-500">Loading…</p></div>

  return (
    <div className="space-y-4 max-w-3xl">
      <DemoNotice />

      <Link to="/admin/client-groups" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> All groups
      </Link>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Group details</h3>
          <button onClick={archive} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1">
            <Trash2 className="w-4 h-4" /> Archive group
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([key, label, type]) => (
            <label key={key} className={type === 'textarea' ? 'sm:col-span-2' : ''}>
              <span className="text-xs text-gray-500 block mb-1">{label}</span>
              {type === 'textarea'
                ? <textarea value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} className="input-field" rows={2} />
                : <input type={type} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} className="input-field" />
              }
            </label>
          ))}
          <label>
            <span className="text-xs text-gray-500 block mb-1">Brand profile (applies to all sites in this group)</span>
            <select value={form.brand_settings_id || ''} onChange={e => setForm({ ...form, brand_settings_id: e.target.value || null })} className="input-field">
              <option value="">— inherit global —</option>
              {brandProfiles.map(b => (
                <option key={b.id} value={b.id}>{b.scope === 'global' ? '[Global] ' : ''}{b.name || '(unnamed)'}</option>
              ))}
            </select>
          </label>
          <div className="sm:col-span-2 mt-3 pt-4 border-t border-gray-100">
            <span className="text-xs uppercase tracking-wide text-gray-500 font-medium block mb-2">Platform mode</span>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-brand hover:bg-brand-50/40">
                <input
                  type="radio"
                  name="mode"
                  checked={form.mode !== 'reporting'}
                  onChange={() => setForm({ ...form, mode: 'ordering' })}
                  className="mt-0.5 w-4 h-4"
                />
                <span className="text-sm">
                  <span className="block font-medium text-gray-800">OrderiQ — full ordering + reporting</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Sites can place orders, see order history, activity, and reports. Standard mode.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-brand hover:bg-brand-50/40">
                <input
                  type="radio"
                  name="mode"
                  checked={form.mode === 'reporting'}
                  onChange={() => setForm({ ...form, mode: 'reporting' })}
                  className="mt-0.5 w-4 h-4"
                />
                <span className="text-sm">
                  <span className="block font-medium text-gray-800">ReportiQ — reporting only</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Hides the ordering flow entirely. Sites see Dashboard / Products / Reports / Help.
                    A prominent <em>"Apply for order portal"</em> button lets head office ask to upgrade later.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <label className="sm:col-span-2 flex items-start gap-3 mt-1">
            <input
              type="checkbox"
              checked={form.consignments_enabled !== false}
              onChange={e => setForm({ ...form, consignments_enabled: e.target.checked })}
              className="mt-1 w-4 h-4"
            />
            <span className="text-sm">
              <span className="block font-medium text-gray-800">Consignments required for orders</span>
              <span className="block text-xs text-gray-500 mt-0.5">
                Tick for clients like Away Resorts where each order is broken into consignments
                per recipient (e.g. "Hamper for John Smith, plot 42"). Untick for clients like
                Marstons that order in bulk to one address — the ordering flow will hide the
                consignment step entirely.
              </span>
            </span>
          </label>

          <div className="sm:col-span-2 mt-3 pt-4 border-t border-gray-100">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={!!form.require_approval}
                onChange={e => setForm({ ...form, require_approval: e.target.checked })}
                className="mt-1 w-4 h-4"
              />
              <span className="text-sm">
                <span className="block font-medium text-gray-800">Require head-office approval before Walter Geering receives the order</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  When on, any order placed by a site in this group goes to <em>Awaiting approval</em>.
                  The approver below gets a single-use email link (valid 7 days). Only after they
                  approve is the order sent to Walter Geering.
                </span>
              </span>
            </label>
            {form.require_approval && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 ml-7">
                <label>
                  <span className="text-xs text-gray-500 block mb-1">Approver name (optional)</span>
                  <input
                    type="text"
                    value={form.approver_name || ''}
                    onChange={e => setForm({ ...form, approver_name: e.target.value })}
                    className="input-field"
                    placeholder="e.g. Prashanna Patel"
                  />
                </label>
                <label>
                  <span className="text-xs text-gray-500 block mb-1">Approver email <span className="text-red-500">*</span></span>
                  <input
                    type="email"
                    value={form.approver_email || ''}
                    onChange={e => setForm({ ...form, approver_email: e.target.value })}
                    className="input-field"
                    placeholder="approvals@travelchapter.co.uk"
                    required
                  />
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <SavedIndicator savedAt={savedAt} />
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">Member sites ({sites.length})</h3>
          <button onClick={() => setAdding(a => !a)} className="btn-secondary text-sm">
            <Plus className="w-4 h-4" /> Add site to group
          </button>
        </div>

        {adding && (
          <div className="mb-3 p-3 bg-gray-50 rounded-lg">
            {orphanSites.length === 0
              ? <p className="text-sm text-gray-500">No ungrouped sites available. Either create a new company under this group on the Companies page, or remove a site from another group first.</p>
              : (
                <ul className="divide-y divide-gray-100 bg-white rounded-lg border border-gray-100 max-h-72 overflow-auto">
                  {orphanSites.map(c => (
                    <li key={c.id}>
                      <button type="button" onClick={() => addSite(c.id)} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm font-medium text-gray-800">
                        {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            }
          </div>
        )}

        {sites.length === 0
          ? <p className="text-sm text-gray-500 py-6 text-center">No sites in this group yet.</p>
          : (
            <ul className="divide-y divide-gray-100">
              {sites.map(c => (
                <li key={c.id} className="flex items-center gap-3 py-2">
                  <Building2 className="w-5 h-5 text-brand" />
                  <Link to={`/companies/${c.id}`} className="flex-1 min-w-0 hover:underline">
                    <div className="font-medium text-gray-800 truncate">{c.name}</div>
                    {(c.contact_name || c.contact_email) && (
                      <div className="text-xs text-gray-500 truncate">
                        {[c.contact_name, c.contact_email].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </Link>
                  <button onClick={() => removeSite(c.id)} className="text-gray-400 hover:text-red-600 p-1">
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )
        }
      </div>
    </div>
  )
}
